/**
 * PaginationBar.tsx — Organizations V3 (HCO)
 * Shows "x–y of z" count, prev/next navigation, and page-size selector.
 * Matches v2 UX: sizes 20 / 40 / 60 / 80 / 100.
 * Pattern identical to ExUsProfessionalsV3/components/PaginationBar.tsx.
 */
import React from 'react';
import styles from '../OrganizationsV3.module.css';

interface Props {
    totalCount:       number;
    pageNumber:       number;
    pageSize:         number;
    onPageChange:     (page: number) => void;
    onPageSizeChange: (size: number) => void;
}

const PAGE_SIZES = [20, 40, 60, 80, 100] as const;

export default function PaginationBar({
    totalCount, pageNumber, pageSize, onPageChange, onPageSizeChange,
}: Props) {
    const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));
    const firstItem  = totalCount === 0 ? 0 : (pageNumber - 1) * pageSize + 1;
    const lastItem   = Math.min(pageNumber * pageSize, totalCount);

    return (
        <div className={styles.paginationBar} data-testid="pagination-bar">
            <div className={styles.paginationInfo} data-testid="pagination-info">
                {totalCount === 0
                    ? 'No results'
                    : `${firstItem.toLocaleString()}–${lastItem.toLocaleString()} of ${totalCount.toLocaleString()}`
                }
            </div>

            <div className={styles.paginationControls}>
                <button
                    className={styles.paginationBtn}
                    onClick={() => onPageChange(1)}
                    disabled={pageNumber <= 1}
                    title="First page"
                    data-testid="page-first"
                >
                    «
                </button>
                <button
                    className={styles.paginationBtn}
                    onClick={() => onPageChange(pageNumber - 1)}
                    disabled={pageNumber <= 1}
                    title="Previous page"
                    data-testid="page-prev"
                >
                    ‹
                </button>

                <span style={{ fontSize: 13, color: '#555', padding: '0 6px' }}>
                    {pageNumber} / {totalPages}
                </span>

                <button
                    className={styles.paginationBtn}
                    onClick={() => onPageChange(pageNumber + 1)}
                    disabled={pageNumber >= totalPages}
                    title="Next page"
                    data-testid="page-next"
                >
                    ›
                </button>
                <button
                    className={styles.paginationBtn}
                    onClick={() => onPageChange(totalPages)}
                    disabled={pageNumber >= totalPages}
                    title="Last page"
                    data-testid="page-last"
                >
                    »
                </button>

                <div className={styles.pageSizes}>
                    <span className={styles.pageSizeLabel}>View</span>
                    {PAGE_SIZES.map(size => (
                        <button
                            key={size}
                            className={[
                                styles.pageSizeBtn,
                                size === pageSize ? styles.pageSizeBtnActive : '',
                            ].join(' ')}
                            onClick={() => onPageSizeChange(size)}
                            data-testid={`pagesize-${size}`}
                        >
                            {size}
                        </button>
                    ))}
                </div>
            </div>
        </div>
    );
}
